import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import { carregarConversoesPrincipais, type ConversaoPrincipal } from '../api/conversoes'
import { carregarOrganico, type DadosOrganico, type DiaOrganico, type LinhaBusca } from '../api/organico'
import { AvisoConversao } from '../components/AvisoConversao'
import { ChartEmpty } from '../components/ChartEmpty'
import { ColunasDiarias, type PontoDiario } from '../components/charts/ColunasDiarias'
import { useCorSerieUnica } from '../components/charts/coresPlataforma'
import { TabelaDiaria } from '../components/charts/TabelaDiaria'
import { KpiCard } from '../components/KpiCard'
import { LeituraIA } from '../components/LeituraIA'
import { PainelShell } from '../components/PainelShell'
import { useFiltros } from '../state/FiltrosContext'
import {
  formatarConversoes,
  formatarInteiro,
  formatarNumero,
  formatarPercentual,
  formatarPosicao,
  razao,
} from '../utils/formato'
import { diasDoIntervalo } from '../utils/periodo'

type Soma = Omit<DiaOrganico, 'data' | 'demo'>

const ZERO: Soma = {
  cliques: 0,
  impressoes: 0,
  posicaoPonderada: 0,
  sessoes: 0,
  sessoesEngajadas: 0,
  novosUsuarios: 0,
  conversoes: 0,
  valorConversoes: 0,
}

const somar = (a: Soma, b: Soma): Soma => ({
  cliques: a.cliques + b.cliques,
  impressoes: a.impressoes + b.impressoes,
  posicaoPonderada: a.posicaoPonderada + b.posicaoPonderada,
  sessoes: a.sessoes + b.sessoes,
  sessoesEngajadas: a.sessoesEngajadas + b.sessoesEngajadas,
  novosUsuarios: a.novosUsuarios + b.novosUsuarios,
  conversoes: a.conversoes + b.conversoes,
  valorConversoes: a.valorConversoes + b.valorConversoes,
})

/** Posição média de um conjunto de dias: ponderada pelas impressões, nunca média simples. */
const posicao = (s: Soma) => razao(s.posicaoPonderada, s.impressoes)

const variacao = (atual: number | null, anterior: number | null) =>
  atual == null || anterior == null || anterior === 0 ? null : ((atual - anterior) / Math.abs(anterior)) * 100

const traco = (v: number | null, f: (n: number) => string) => (v == null ? '—' : f(v))

type Kpi = { label: string; valor: string; delta: number | null; spark: number[]; hint?: string; melhorQuando?: 'desce' }

export function OrganicoPage() {
  const { intervalo, marcaId, marcas } = useFiltros()
  const cor = useCorSerieUnica()
  const [modo, setModo] = useState<'grafico' | 'tabela'>('grafico')
  const [dados, setDados] = useState<DadosOrganico | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [principais, setPrincipais] = useState<ConversaoPrincipal[]>([])

  useEffect(() => {
    carregarConversoesPrincipais().then(setPrincipais).catch(() => setPrincipais([]))
  }, [])

  const filtro = useMemo(() => ({ intervalo, marcaId }), [intervalo, marcaId])

  useEffect(() => {
    let cancelado = false
    setCarregando(true)
    carregarOrganico(filtro)
      .then((d) => {
        if (cancelado) return
        setDados(d)
        setErro(null)
      })
      .catch((e: Error) => {
        if (!cancelado) setErro(e.message)
      })
      .finally(() => {
        if (!cancelado) setCarregando(false)
      })
    return () => {
      cancelado = true
    }
  }, [filtro])

  // Um ponto por dia do intervalo; dia sem dado = zero.
  const dias = useMemo(() => {
    const porDia = new Map<string, Soma>(diasDoIntervalo(intervalo).map((d) => [d, ZERO]))
    for (const l of dados?.serie ?? []) {
      const atual = porDia.get(l.data)
      if (atual) porDia.set(l.data, somar(atual, l))
    }
    return [...porDia.entries()].map(([data, soma]) => ({ data, ...soma }))
  }, [dados, intervalo])

  const atual = useMemo(() => dias.reduce((acc, d) => somar(acc, d), ZERO), [dias])
  const anterior = dados?.anterior
  const temDemo = Boolean(dados?.serie.some((l) => l.demo) || anterior?.demo)
  const vazio = dados != null && dados.serie.length === 0
  const nomeConversao = marcaId ? (principais.find((p) => p.marcaId === marcaId)?.nome ?? 'Conversões') : 'Conversões'
  const marcasNoRecorte = marcaId ? marcas.filter((m) => m.id === marcaId) : marcas

  // Sem impressões nos dois períodos, o Search Console não está conectado (ou não tem dado):
  // os números da busca ficam em "—", não em zero.
  const semBusca = dados != null && atual.impressoes === 0 && (anterior?.impressoes ?? 0) === 0
  const serieDe = (f: (s: Soma) => number | null) => dias.map((d) => f(d) ?? 0)
  const pontos = (chave: string, f: (s: Soma) => number): PontoDiario[] => dias.map((d) => ({ data: d.data, [chave]: f(d) }))

  const kpisBusca: Kpi[] = [
    {
      label: 'Cliques na busca',
      valor: formatarNumero(atual.cliques),
      delta: variacao(atual.cliques, anterior?.cliques ?? null),
      spark: serieDe((s) => s.cliques),
    },
    {
      label: 'Impressões na busca',
      valor: formatarNumero(atual.impressoes),
      delta: variacao(atual.impressoes, anterior?.impressoes ?? null),
      spark: serieDe((s) => s.impressoes),
    },
    {
      label: 'CTR da busca',
      valor: traco(razao(atual.cliques, atual.impressoes), formatarPercentual),
      delta: variacao(razao(atual.cliques, atual.impressoes), anterior ? razao(anterior.cliques, anterior.impressoes) : null),
      spark: serieDe((s) => razao(s.cliques, s.impressoes)),
    },
    {
      label: 'Posição média',
      valor: traco(posicao(atual), formatarPosicao),
      delta: variacao(posicao(atual), anterior?.posicaoMedia ?? null),
      // Posição 1 é o topo do Google: cair de 9 para 8 é melhora.
      melhorQuando: 'desce' as const,
      spark: serieDe(posicao),
      hint: 'Ponderada pelas impressões',
    },
  ]
  const kpis: Kpi[] = [
    ...kpisBusca.map((k) => (semBusca ? { ...k, valor: '—', delta: null, spark: [], hint: 'Sem dados do Search Console' } : k)),
    {
      label: 'Sessões da busca orgânica',
      valor: formatarNumero(atual.sessoes),
      delta: variacao(atual.sessoes, anterior?.sessoes ?? null),
      spark: serieDe((s) => s.sessoes),
    },
    {
      label: 'Taxa de engajamento',
      valor: traco(razao(atual.sessoesEngajadas, atual.sessoes), formatarPercentual),
      delta: variacao(razao(atual.sessoesEngajadas, atual.sessoes), anterior ? razao(anterior.sessoesEngajadas, anterior.sessoes) : null),
      spark: serieDe((s) => razao(s.sessoesEngajadas, s.sessoes)),
    },
    {
      label: nomeConversao,
      valor: formatarConversoes(atual.conversoes),
      delta: variacao(atual.conversoes, anterior?.conversoes ?? null),
      spark: serieDe((s) => s.conversoes),
      hint: marcaId ? 'Eventos do GA4 na busca orgânica' : 'Soma a conversão principal de cada marca',
    },
    {
      // Não é "taxa": o GA4 conta eventos, e uma sessão pode ter vários — passa de 100%.
      label: 'Conversões por sessão',
      valor: traco(razao(atual.conversoes, atual.sessoes), formatarPercentual),
      delta: variacao(razao(atual.conversoes, atual.sessoes), anterior ? razao(anterior.conversoes, anterior.sessoes) : null),
      spark: serieDe((s) => razao(s.conversoes, s.sessoes)),
      hint: 'Pode passar de 100%: uma sessão pode ter várias conversões',
    },
  ]

  const graficos = [
    { titulo: 'Cliques na busca', chave: 'cliques', f: (s: Soma) => s.cliques, vazio: semBusca },
    { titulo: 'Sessões da busca orgânica', chave: 'sessoes', f: (s: Soma) => s.sessoes, vazio: false },
  ]

  return (
    <PainelShell titulo="Orgânico" fontes="Search Console · GA4 (busca orgânica)">
      {temDemo ? (
        <Alert severity="warning" variant="outlined">
          <strong>Dados de demonstração.</strong> Números fictícios, só para testar o painel — saem quando os
          coletores estiverem ligados às contas reais.
        </Alert>
      ) : null}

      <AvisoConversao marcas={marcasNoRecorte} principais={principais} plataformasDaFonte={['ga4']} nomeDaFonte="do GA4" />

      {erro ? <Alert severity="error">Não foi possível carregar o painel: {erro}</Alert> : null}

      {vazio ? (
        <Paper variant="outlined" sx={{ pb: 6 }}>
          <ChartEmpty title="Sem dados do orgânico neste recorte" />
        </Paper>
      ) : (
        // Ao trocar filtro, a tela anterior fica esmaecida em vez de piscar.
        <Stack spacing={2} sx={{ opacity: carregando && dados ? 0.55 : 1, transition: 'opacity 160ms ease' }}>
          <LeituraIA painel="organico" />

          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
            }}
          >
            {kpis.map((k) => (
              <KpiCard
                key={k.label}
                label={k.label}
                value={k.valor}
                deltaPct={k.delta}
                deltaSufixo="vs período anterior"
                melhorQuando={k.melhorQuando}
                sparkline={k.spark}
                hint={k.hint}
                loading={!dados}
              />
            ))}
          </Box>

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2" color="text.secondary">
              Por dia
            </Typography>
            <ToggleButtonGroup exclusive size="small" value={modo} onChange={(_, v) => v && setModo(v)} aria-label="Visualização">
              <ToggleButton value="grafico" sx={{ textTransform: 'none', px: 1.5 }}>
                Gráfico
              </ToggleButton>
              <ToggleButton value="tabela" sx={{ textTransform: 'none', px: 1.5 }}>
                Tabela
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {modo === 'grafico' ? (
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' } }}>
              {graficos.map((g) =>
                g.vazio ? (
                  <Paper key={g.chave} variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: '"Outfit", sans-serif' }}>
                      {g.titulo}
                    </Typography>
                    <ChartEmpty title="Sem dados do Search Console neste recorte" hint="Conecte a propriedade da marca em Configurações → Marcas." suggestPeriod={false} dense />
                  </Paper>
                ) : (
                  <ColunasDiarias
                    key={g.chave}
                    titulo={g.titulo}
                    pontos={pontos(g.chave, g.f)}
                    series={[{ chave: g.chave, nome: g.titulo, cor }]}
                    formatar={formatarInteiro}
                    formatarEixo={formatarNumero}
                  />
                ),
              )}
            </Box>
          ) : (
            <TabelaDiaria
              colunas={[{ chave: 'valor', nome: 'Valor' }]}
              series={graficos.filter((g) => !g.vazio).map((g) => ({ titulo: g.titulo, pontos: pontos('valor', g.f), formatar: formatarInteiro }))}
            />
          )}

          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' } }}>
            <TabelaBusca titulo="Consultas" linhas={dados?.consultas ?? []} mostrarMarca={!marcaId} tipo="consulta" />
            <TabelaBusca titulo="Páginas" linhas={dados?.paginas ?? []} mostrarMarca={!marcaId} tipo="pagina" />
          </Box>
        </Stack>
      )}
    </PainelShell>
  )
}

/** Caminho da página sem protocolo e domínio; a URL completa fica no tooltip. */
function caminho(url: string) {
  try {
    const u = new URL(url)
    return u.pathname + u.search || '/'
  } catch {
    return url
  }
}

function TabelaBusca({
  titulo,
  linhas,
  mostrarMarca,
  tipo,
}: {
  titulo: string
  linhas: LinhaBusca[]
  mostrarMarca: boolean
  tipo: 'consulta' | 'pagina'
}) {
  const num = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const

  return (
    <Paper variant="outlined">
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: '"Outfit", sans-serif' }}>
          {titulo}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          As {linhas.length} com mais cliques
        </Typography>
      </Stack>
      {linhas.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }}>
          Nenhuma {tipo === 'consulta' ? 'consulta' : 'página'} com cliques neste recorte.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{tipo === 'consulta' ? 'Consulta' : 'Página'}</TableCell>
                {mostrarMarca ? <TableCell>Marca</TableCell> : null}
                <TableCell align="right">Cliques</TableCell>
                <TableCell align="right">Impressões</TableCell>
                <TableCell align="right">CTR</TableCell>
                <TableCell align="right">Posição</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {linhas.map((l) => (
                <TableRow key={`${l.marcaId}-${l.chave}`} hover>
                  <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {tipo === 'pagina' ? (
                      <Tooltip title={l.chave}>
                        <span>{caminho(l.chave)}</span>
                      </Tooltip>
                    ) : (
                      l.chave
                    )}
                  </TableCell>
                  {mostrarMarca ? <TableCell sx={{ whiteSpace: 'nowrap' }}>{l.marca}</TableCell> : null}
                  <TableCell align="right" sx={num}>
                    {formatarInteiro(l.cliques)}
                  </TableCell>
                  <TableCell align="right" sx={num}>
                    {formatarInteiro(l.impressoes)}
                  </TableCell>
                  <TableCell align="right" sx={num}>
                    {traco(razao(l.cliques, l.impressoes), formatarPercentual)}
                  </TableCell>
                  <TableCell align="right" sx={num}>
                    {traco(l.posicaoMedia, formatarPosicao)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  )
}
